import { ryffQuestions, ryffItemScore } from '../../../assessments/ryffQuestions';
import { getAssessmentExamples } from '../../assessmentExamples';
import { ensureSession, fetchProgress, resumePoint, saveProgress, completeSession } from '../sessionApi';
import { guidanceOfficeFact, inTurn, NOTED, HONEST } from './common';

const TOTAL = ryffQuestions.length;

// Stored values follow the SPARQ form ANNA uses: 1 = Strongly agree … 7 = Strongly disagree.
const LABELS = {
  1: { en: 'Strongly agree', fil: 'Lubos na sumasang-ayon' },
  2: { en: 'Somewhat agree', fil: 'Medyo sumasang-ayon' },
  3: { en: 'Slightly agree', fil: 'Bahagyang sumasang-ayon' },
  4: { en: 'Neither agree nor disagree', fil: 'Hindi sang-ayon o salungat' },
  5: { en: 'Slightly disagree', fil: 'Bahagyang hindi sumasang-ayon' },
  6: { en: 'Somewhat disagree', fil: 'Medyo hindi sumasang-ayon' },
  7: { en: 'Strongly disagree', fil: 'Lubos na hindi sumasang-ayon' },
};
// The words on the buttons, grouped into agree / neither / disagree rows
const BUTTON = {
  1: { en: 'Strongly', fil: 'Lubos' },
  2: { en: 'Somewhat', fil: 'Medyo' },
  3: { en: 'Slightly', fil: 'Bahagya' },
  4: { en: 'Neither agree nor disagree', fil: 'Hindi sang-ayon o salungat' },
  5: { en: 'Slightly', fil: 'Bahagya' },
  6: { en: 'Somewhat', fil: 'Medyo' },
  7: { en: 'Strongly', fil: 'Lubos' },
};

const DIMENSIONS = ['autonomy', 'environmental_mastery', 'personal_growth', 'positive_relations', 'purpose_in_life', 'self_acceptance'];

const DESCRIPTIONS = {
  autonomy: {
    High: 'You have a strong sense of self-determination and independence. You resist social pressures and regulate behavior from within.',
    Moderate: "You have a moderate sense of autonomy and self-direction, with some concern for others' expectations.",
    Low: "You may benefit from developing greater self-confidence and independence. You tend to be concerned about others' expectations and evaluations.",
  },
  environmental_mastery: {
    High: 'You effectively manage your environment and daily responsibilities. You have a sense of mastery and competence in managing complex environments.',
    Moderate: "You generally handle life's demands reasonably well, with some difficulty in managing everyday affairs.",
    Low: 'You may find it challenging to manage external demands and opportunities. You feel unable to change or improve your surrounding context.',
  },
  personal_growth: {
    High: 'You have a strong sense of continued development and self-improvement. You see yourself as growing and expanding over time.',
    Moderate: 'You show moderate interest in personal development, with some sense of improvement over time.',
    Low: 'You may benefit from seeking new experiences and challenges. You sense personal stagnation and lack of improvement over time.',
  },
  positive_relations: {
    High: 'You have warm, satisfying, trusting relationships with others. You are capable of strong empathy, affection, and intimacy.',
    Moderate: 'You maintain reasonably good relationships with others, with some difficulty in being warm and open.',
    Low: 'You may benefit from working on building closer relationships. You have few close, trusting relationships and find it difficult to be warm and open.',
  },
  purpose_in_life: {
    High: 'You have a strong sense of direction and meaning in life. You have goals and a sense of directedness about your present and past life.',
    Moderate: 'You have some sense of purpose and direction, with occasional feelings of aimlessness.',
    Low: 'You may benefit from exploring your values and life goals. You lack a sense of meaning in life and have few goals or aims.',
  },
  self_acceptance: {
    High: 'You have a positive attitude toward yourself and your past. You acknowledge and accept multiple aspects of yourself, including good and bad qualities.',
    Moderate: 'You generally accept yourself, with some reservations about your past life and personal qualities.',
    Low: 'You may benefit from developing greater self-compassion and acceptance. You feel dissatisfied with yourself and disappointed with past life events.',
  },
};

/** The response map the backend stores: { [questionId]: { value, score, dimension, reverse } }. */
export const ryffResponseMap = (answers) => {
  const map = {};
  ryffQuestions.forEach((q, i) => {
    const value = Number(answers[i]);
    if (!value) return;
    // Reverse-scored items use SPARQ's (7 + 1) − response; the server scores the same way on completion
    map[q.id] = { value, score: ryffItemScore(q, value), dimension: q.dimension, reverse: q.reverse };
  });
  return map;
};

export const ryffScores = (answers) => {
  const scores = Object.fromEntries(DIMENSIONS.map((d) => [d, 0]));
  Object.values(ryffResponseMap(answers)).forEach((r) => {
    scores[r.dimension] += r.score;
  });
  return scores;
};

const levelOf = (score) => (score <= 20 ? 'Low' : score <= 34 ? 'Moderate' : 'High');

export const ryffInterpretation = (scores) =>
  Object.fromEntries(
    DIMENSIONS.map((d) => [
      d,
      { score: scores[d], level: levelOf(scores[d]), description: DESCRIPTIONS[d][levelOf(scores[d])], maxScore: 49, minScore: 7 },
    ]),
  );

const overallLevel = (total) => (total <= 125 ? 'Low' : total <= 209 ? 'Moderate' : 'High');

/** Saved responses (either shape) → answers by question index. */
const answersFromMap = (map) =>
  ryffQuestions.map((q) => {
    const entry = map?.[q.id] ?? map?.[String(q.id)];
    return entry && entry.value ? Number(entry.value) : undefined;
  });

const ryff = {
  key: 'ryff',
  total: TOTAL,
  feedbackCategory: 'ryff',
  followupReason: 'ryff_followup',
  resultsTab: 'ryff',
  names: {
    title: { en: 'Well-being check', fil: 'Well-being check' },
    short: { en: 'Well-being', fil: 'Well-being' },
    code: 'Ryff scale',
  },
  greeting: {
    en: "👋 Hello! Let's start the Ryff Scale. These questions help us understand how you see yourself and your life right now.",
    fil: '👋 Hello! Simulan natin ang Ryff Scale. Makakatulong ang mga tanong na ito para maunawaan namin kung paano mo nakikita ang sarili mo at ang buhay mo ngayon.',
  },
  facts: (lang, school) => [
    { icon: 'list', text: lang === 'fil' ? `${TOTAL} pahayag tungkol sa iyo` : `${TOTAL} statements about you` },
    guidanceOfficeFact(lang, school),
  ],
  introNote: {
    en: 'Some statements are worded the opposite way on purpose. Read each one carefully.',
    fil: 'May mga pahayag na sadyang baligtad ang pagkakasulat. Basahin nang mabuti ang bawat isa.',
  },
  layout: 'agree',
  scale: [1, 2, 3, 4, 5, 6, 7].map((value) => ({ value, label: LABELS[value], button: BUTTON[value], sub: null })),
  prompt: { en: 'How much do you agree?', fil: 'Gaano ka sumasang-ayon?' },
  questionText: (i) => ryffQuestions[i].text,
  examples: (i, lang) => {
    const q = ryffQuestions[i];
    return (lang === 'fil' ? q.examplesFil : q.examples) || getAssessmentExamples('ryff', q.id, lang) || [];
  },
  echo: (value, lang) => LABELS[value]?.[lang === 'fil' ? 'fil' : 'en'] || String(value),
  checkpoints: {
    10: { en: "That's 10. 32 more, at your own pace.", fil: '10 na. 32 pa, dahan-dahan lang.' },
    21: { en: 'Halfway! Keep going at your own pace.', fil: 'Kalahati na! Tuloy lang sa sarili mong bilis.' },
    32: { en: 'Just 10 left. Almost there.', fil: '10 na lang. Malapit na.' },
  },
  isWorrying: () => false,
  reply: (i, value, lang, n) => {
    // A low-well-being answer (worth 2 points or fewer) gets the gentler reply
    if (ryffItemScore(ryffQuestions[i], Number(value)) <= 2) return { care: true, text: inTurn(HONEST, lang, n) };
    return { text: inTurn(NOTED, lang, n) };
  },

  // The chat starts Ryff's session itself and passes it in. The server's copy wins, so a chat
  // closed and reopened in the same visit picks up where the student left off.
  load: async ({ sessionId, savedProgress }) => {
    try {
      const point = resumePoint(await fetchProgress('ryff'), TOTAL);
      if (point.sessionId && (!sessionId || String(point.sessionId) === String(sessionId))) {
        return {
          sessionId: point.sessionId,
          nextIndex: point.nextIndex,
          answers: point.responses ? answersFromMap(point.responses) : [],
        };
      }
    } catch {
      // Fall back to what the chat passed in
    }
    const saved = savedProgress?.responses || {};
    if (sessionId && Object.keys(saved).length > 0) {
      const nextIndex = Math.min(Number(savedProgress.currentQuestion) || 0, TOTAL - 1);
      return { sessionId, nextIndex, answers: answersFromMap(saved) };
    }
    if (sessionId) return { sessionId, nextIndex: 0, answers: [] };
    const point = resumePoint(await fetchProgress('ryff'), TOTAL);
    return { sessionId: point.sessionId, nextIndex: point.nextIndex, answers: point.responses ? answersFromMap(point.responses) : [] };
  },
  save: async ({ sessionId, nextIndex, answers, answeredAt }) => {
    const id = await ensureSession('ryff', TOTAL, sessionId);
    await saveProgress({
      session_id: id,
      assessment_type: 'ryff',
      current_step: nextIndex,
      answered_at: answeredAt,
      responses: ryffResponseMap(answers),
      scores: ryffScores(answers),
    });
    return id;
  },
  complete: async ({ sessionId, answers }) => {
    const id = await ensureSession('ryff', TOTAL, sessionId);
    const scores = ryffScores(answers);
    const interpretation = ryffInterpretation(scores);
    const total = Object.values(scores).reduce((sum, s) => sum + s, 0);
    await completeSession(id, {
      assessment_type: 'ryff',
      final_responses: ryffResponseMap(answers),
      scores: { ...scores, total_wellbeing: total, overall_level: overallLevel(total), interpretation },
      interpretation,
    });
    return id;
  },
};

export default ryff;
